/**
 * FikoGDTSymbol - Geometrik Dimensioning & Tolerancing (GD&T) Sistemi
 *
 * CATIA/SolidWorks tarzında GD&T sembolleri ve tolerans kutucukları.
 * ISO 1101 / ASME Y14.5 standartlarına uygun.
 *
 * Özellikler:
 * - 14 GD&T sembolü (düzlemsellik, diklik, paralellik, dairesellik, vb.)
 * - Datum referansları (A, B, C kutucukları)
 * - Feature Control Frame (FCF) kutucukları
 * - Leader çizgileri
 * - İnteraktif sürükle-bırak
 * - Hover efektleri
 *
 * @author FikoYT CAD Team
 * @version 1.0.0
 */

import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { getCanvasResolutionVec2 } from '../../../FikoCanvasResolution';

// ============================================================================
// ENUMS & TYPES
// ============================================================================

/**
 * GD&T Sembol Tipleri (ISO 1101 / ASME Y14.5)
 */
export enum GDTSymbolType {
    // Form toleransları
    FLATNESS = 'FLATNESS',                   // Düzlemsellik ⏥
    STRAIGHTNESS = 'STRAIGHTNESS',           // Doğrusallık ⏤
    CIRCULARITY = 'CIRCULARITY',             // Dairesellik ○
    CYLINDRICITY = 'CYLINDRICITY',           // Silindiriklik ⌭

    // Orientation toleransları
    PERPENDICULARITY = 'PERPENDICULARITY',   // Diklik ⊥
    PARALLELISM = 'PARALLELISM',             // Paralellik //
    ANGULARITY = 'ANGULARITY',               // Açısallık ∠

    // Location toleransları
    POSITION = 'POSITION',                   // Pozisyon ⌖
    CONCENTRICITY = 'CONCENTRICITY',         // Eş merkezlilik ◎
    SYMMETRY = 'SYMMETRY',                   // Simetri ⌯

    // Runout toleransları
    CIRCULAR_RUNOUT = 'CIRCULAR_RUNOUT',     // Dairesel runout ↗
    TOTAL_RUNOUT = 'TOTAL_RUNOUT',           // Toplam runout ↗↗

    // Profile toleransları
    PROFILE_LINE = 'PROFILE_LINE',           // Çizgi profili ⌒
    PROFILE_SURFACE = 'PROFILE_SURFACE'      // Yüzey profili ⌓
}

/**
 * Malzeme Koşulu Modifikatörleri
 */
export enum MaterialCondition {
    NONE = 'NONE',
    MMC = 'MMC',       // Maximum Material Condition (M)
    LMC = 'LMC',       // Least Material Condition (L)
    RFS = 'RFS'        // Regardless of Feature Size (S)
}

/**
 * GD&T Sembol Unicode Karakterleri
 */
export const GDT_SYMBOLS: Record<GDTSymbolType, string> = {
    [GDTSymbolType.FLATNESS]: '⏥',
    [GDTSymbolType.STRAIGHTNESS]: '⏤',
    [GDTSymbolType.CIRCULARITY]: '○',
    [GDTSymbolType.CYLINDRICITY]: '⌭',
    [GDTSymbolType.PERPENDICULARITY]: '⊥',
    [GDTSymbolType.PARALLELISM]: '∥',
    [GDTSymbolType.ANGULARITY]: '∠',
    [GDTSymbolType.POSITION]: '⌖',
    [GDTSymbolType.CONCENTRICITY]: '◎',
    [GDTSymbolType.SYMMETRY]: '⌯',
    [GDTSymbolType.CIRCULAR_RUNOUT]: '↗',
    [GDTSymbolType.TOTAL_RUNOUT]: '⇗',
    [GDTSymbolType.PROFILE_LINE]: '⌒',
    [GDTSymbolType.PROFILE_SURFACE]: '⌓'
};

/**
 * GD&T Stil Konfigürasyonu
 */
export interface GDTStyleConfig {
    // Renkler
    frameColor: number;           // Çerçeve rengi
    textColor: string;            // Metin rengi
    backgroundColor: number;      // Arka plan rengi
    leaderColor: number;          // Leader çizgi rengi

    // Çizgi
    lineWidth: number;

    // Boyutlar
    cellHeight: number;           // Hücre yüksekliği
    cellPadding: number;          // İç boşluk
    fontSize: number;
    fontFamily: string;

    // Leader
    leaderArrowSize: number;
    leaderLineWidth: number;
}

/**
 * Feature Control Frame Konfigürasyonu
 */
export interface FCFConfig {
    symbolType: GDTSymbolType;
    tolerance: number;
    materialCondition?: MaterialCondition;
    datumRefs?: string[];         // ['A', 'B', 'C'] gibi
    diameter?: boolean;           // Ø tolerans mı?
}

/**
 * Datum Konfigürasyonu
 */
export interface DatumConfig {
    label: string;                // A, B, C, vb.
    attachPoint: THREE.Vector3;   // Bağlantı noktası
}

/**
 * İnteraktif özellikler
 */
export interface InteractiveConfig {
    draggable: boolean;
    selectable: boolean;
    hoverable: boolean;
    snapToGrid?: number;          // Grid snap değeri
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

export const DEFAULT_GDT_STYLE: GDTStyleConfig = {
    frameColor: 0x000000,         // Siyah çerçeve
    textColor: '#000000',
    backgroundColor: 0xffffff,    // Beyaz arka plan
    leaderColor: 0x000000,
    lineWidth: 1.5,
    cellHeight: 8,
    cellPadding: 2,
    fontSize: 10,
    fontFamily: 'Arial, sans-serif',
    leaderArrowSize: 2,
    leaderLineWidth: 1
};

export const CATIA_GDT_STYLE: GDTStyleConfig = {
    frameColor: 0x00cc00,         // CATIA yeşili
    textColor: '#00cc00',
    backgroundColor: 0x1a1a2e,    // Koyu mavi arka plan
    leaderColor: 0x00cc00,
    lineWidth: 1.5,
    cellHeight: 8,
    cellPadding: 2,
    fontSize: 10,
    fontFamily: 'Arial, sans-serif',
    leaderArrowSize: 2,
    leaderLineWidth: 1
};

// ============================================================================
// GD&T SYMBOL CLASS
// ============================================================================

/**
 * FikoGDTSymbol - Feature Control Frame ve GD&T Sembolleri
 */
export class FikoGDTSymbol extends THREE.Group {
    readonly symbolType = 'FikoGDTSymbol';

    // Konfigürasyon
    private fcfConfig: FCFConfig;
    private style: GDTStyleConfig;
    private interactive: InteractiveConfig;

    // Pozisyon
    private attachPoint: THREE.Vector3 = new THREE.Vector3();
    private framePosition: THREE.Vector3 = new THREE.Vector3();

    // Bileşenler
    private frameSprite: THREE.Sprite | null = null;
    private leaderLine: Line2 | null = null;
    private leaderArrow: THREE.Mesh | null = null;

    // İnteraktif durum
    private _isSelected: boolean = false;
    private _isHovered: boolean = false;
    private _isDragging: boolean = false;
    private dragOffset: THREE.Vector3 = new THREE.Vector3();

    // Event callbacks
    private onDragStart?: (symbol: FikoGDTSymbol) => void;
    private onDrag?: (symbol: FikoGDTSymbol, position: THREE.Vector3) => void;
    private onDragEnd?: (symbol: FikoGDTSymbol) => void;
    private onSelect?: (symbol: FikoGDTSymbol) => void;
    private onHover?: (symbol: FikoGDTSymbol, isHovered: boolean) => void;

    constructor(
        fcfConfig: FCFConfig,
        attachPoint: THREE.Vector3,
        frameOffset: THREE.Vector3 = new THREE.Vector3(20, 10, 0),
        style: Partial<GDTStyleConfig> = {},
        interactive: Partial<InteractiveConfig> = {}
    ) {
        super();

        this.fcfConfig = fcfConfig;
        this.attachPoint.copy(attachPoint);
        this.framePosition.copy(attachPoint).add(frameOffset);

        this.style = { ...CATIA_GDT_STYLE, ...style };
        this.interactive = {
            draggable: true,
            selectable: true,
            hoverable: true,
            snapToGrid: undefined,
            ...interactive
        };

        this.renderOrder = 1001;
        this.frustumCulled = false;

        this.build();
    }

    // ========================================================================
    // PUBLIC API
    // ========================================================================

    /**
     * Seçili mi?
     */
    public get isSelected(): boolean {
        return this._isSelected;
    }

    /**
     * Hover durumu
     */
    public get isHovered(): boolean {
        return this._isHovered;
    }

    /**
     * Sürükleniyor mu?
     */
    public get isDragging(): boolean {
        return this._isDragging;
    }

    /**
     * Seç/Seçimi kaldır
     */
    public setSelected(selected: boolean): void {
        if (this._isSelected !== selected) {
            this._isSelected = selected;
            this.updateVisualState();
            if (selected && this.onSelect) {
                this.onSelect(this);
            }
        }
    }

    /**
     * Hover durumunu ayarla
     */
    public setHovered(hovered: boolean): void {
        if (!this.interactive.hoverable) return;

        if (this._isHovered !== hovered) {
            this._isHovered = hovered;
            this.updateVisualState();
            if (this.onHover) {
                this.onHover(this, hovered);
            }
        }
    }

    /**
     * Sürüklemeyi başlat
     */
    public startDrag(mousePosition: THREE.Vector3): void {
        if (!this.interactive.draggable) return;

        this._isDragging = true;
        this.dragOffset.copy(this.framePosition).sub(mousePosition);

        if (this.onDragStart) {
            this.onDragStart(this);
        }
    }

    /**
     * Sürükle
     */
    public drag(mousePosition: THREE.Vector3): void {
        if (!this._isDragging) return;

        const newPosition = mousePosition.clone().add(this.dragOffset);

        // Grid snap
        if (this.interactive.snapToGrid) {
            const snap = this.interactive.snapToGrid;
            newPosition.x = Math.round(newPosition.x / snap) * snap;
            newPosition.y = Math.round(newPosition.y / snap) * snap;
            newPosition.z = Math.round(newPosition.z / snap) * snap;
        }

        this.setFramePosition(newPosition);

        if (this.onDrag) {
            this.onDrag(this, newPosition);
        }
    }

    /**
     * Sürüklemeyi bitir
     */
    public endDrag(): void {
        if (!this._isDragging) return;

        this._isDragging = false;

        if (this.onDragEnd) {
            this.onDragEnd(this);
        }
    }

    /**
     * Frame pozisyonunu ayarla
     */
    public setFramePosition(position: THREE.Vector3): void {
        this.framePosition.copy(position);
        this.updateLeaderLine();

        if (this.frameSprite) {
            this.frameSprite.position.copy(position);
        }
    }

    /**
     * Bağlantı noktasını ayarla
     */
    public setAttachPoint(point: THREE.Vector3): void {
        this.attachPoint.copy(point);
        this.updateLeaderLine();
    }

    /**
     * Event listener'ları ayarla
     */
    public setEventListeners(listeners: {
        onDragStart?: (symbol: FikoGDTSymbol) => void;
        onDrag?: (symbol: FikoGDTSymbol, position: THREE.Vector3) => void;
        onDragEnd?: (symbol: FikoGDTSymbol) => void;
        onSelect?: (symbol: FikoGDTSymbol) => void;
        onHover?: (symbol: FikoGDTSymbol, isHovered: boolean) => void;
    }): void {
        this.onDragStart = listeners.onDragStart;
        this.onDrag = listeners.onDrag;
        this.onDragEnd = listeners.onDragEnd;
        this.onSelect = listeners.onSelect;
        this.onHover = listeners.onHover;
    }

    /**
     * Stili güncelle
     */
    public updateStyle(style: Partial<GDTStyleConfig>): void {
        this.style = { ...this.style, ...style };
        this.rebuild();
    }

    /**
     * Yeniden oluştur
     */
    public rebuild(): void {
        this.dispose();
        this.build();
    }

    /**
     * Temizlik
     */
    public dispose(): void {
        if (this.frameSprite) {
            const mat = this.frameSprite.material as THREE.SpriteMaterial;
            if (mat.map) mat.map.dispose();
            mat.dispose();
            this.remove(this.frameSprite);
            this.frameSprite = null;
        }

        if (this.leaderLine) {
            this.leaderLine.geometry.dispose();
            (this.leaderLine.material as THREE.Material).dispose();
            this.remove(this.leaderLine);
            this.leaderLine = null;
        }

        if (this.leaderArrow) {
            this.leaderArrow.geometry.dispose();
            (this.leaderArrow.material as THREE.Material).dispose();
            this.remove(this.leaderArrow);
            this.leaderArrow = null;
        }
    }

    // ========================================================================
    // PRIVATE - BUILD
    // ========================================================================

    private build(): void {
        this.createFeatureControlFrame();
        this.createLeaderLine();
        this.createLeaderArrow();
    }

    /**
     * Feature Control Frame (FCF) oluştur
     */
    private createFeatureControlFrame(): void {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        // Hücre sayısını hesapla
        const cells: string[] = [];

        // 1. Sembol hücresi
        cells.push(GDT_SYMBOLS[this.fcfConfig.symbolType]);

        // 2. Tolerans hücresi
        let toleranceText = '';
        if (this.fcfConfig.diameter) {
            toleranceText = 'Ø';
        }
        // GD&T sapmaları tipik olarak 0.0X mm mertebesinde — 1 hane "0.0" gösterir, 3 hane gerçek değeri verir
        toleranceText += this.fcfConfig.tolerance.toFixed(3);
        if (this.fcfConfig.materialCondition && this.fcfConfig.materialCondition !== MaterialCondition.NONE) {
            const mcSymbol = this.fcfConfig.materialCondition === MaterialCondition.MMC ? 'Ⓜ' :
                            this.fcfConfig.materialCondition === MaterialCondition.LMC ? 'Ⓛ' : 'Ⓢ';
            toleranceText += mcSymbol;
        }
        cells.push(toleranceText);

        // 3. Datum referansları
        if (this.fcfConfig.datumRefs) {
            this.fcfConfig.datumRefs.forEach(datum => {
                cells.push(datum);
            });
        }

        // 🎯 Her hücreyi METNİNE göre boyutla (sabit genişlik metni taşırıyordu)
        const ss = 4; // supersample faktörü (keskin metin)
        const fontPx = this.style.fontSize * 3;
        ctx.font = `bold ${fontPx}px ${this.style.fontFamily}`; // ölçüm için fontu ayarla
        const padX = this.style.cellPadding * 2 * ss;
        const minHucre = this.style.cellHeight * 2 * ss;
        const frameHeight = (this.style.cellHeight + this.style.cellPadding * 2) * ss;
        const hucreGenislikler = cells.map(c => Math.max(minHucre, ctx.measureText(c).width + padX * 2));
        const frameWidth = hucreGenislikler.reduce((a, b) => a + b, 0);
        const margin = 8;

        // NOT: canvas.width/height atanınca context sıfırlanır → font aşağıda yeniden ayarlanır
        canvas.width = Math.ceil(frameWidth + margin * 2);
        canvas.height = Math.ceil(frameHeight + margin * 2);

        const startX = margin;
        const startY = margin;

        // Arka plan
        ctx.fillStyle = this._isSelected ? '#ffff00' :
                        this._isHovered ? '#e0e0e0' :
                        `#${this.style.backgroundColor.toString(16).padStart(6, '0')}`;
        ctx.fillRect(startX, startY, frameWidth, frameHeight);

        // Çerçeve
        ctx.strokeStyle = this.style.textColor;
        ctx.lineWidth = this.style.lineWidth * 2;
        ctx.strokeRect(startX, startY, frameWidth, frameHeight);

        // Metin + dikey bölme çizgileri (hücre genişlikleri değişken)
        ctx.font = `bold ${fontPx}px ${this.style.fontFamily}`;
        ctx.fillStyle = this.style.textColor;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        let x = startX;
        cells.forEach((cell, i) => {
            const w = hucreGenislikler[i];
            if (i > 0) {
                ctx.beginPath();
                ctx.moveTo(x, startY);
                ctx.lineTo(x, startY + frameHeight);
                ctx.stroke();
            }
            ctx.fillText(cell, x + w / 2, startY + frameHeight / 2);
            x += w;
        });

        // Texture oluştur
        const texture = new THREE.CanvasTexture(canvas);
        texture.needsUpdate = true;

        const spriteMaterial = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            depthTest: false
        });

        this.frameSprite = new THREE.Sprite(spriteMaterial);
        (this.frameSprite as any).raycast = () => {}; // 🎯 ölçüm tıklamasını engelleme (seçim sprite'a çarpmasın)
        this.frameSprite.position.copy(this.framePosition);

        // 🎯 SABİT EKRAN BOYUTU (ölçüm etiketi gibi): her frame, world-ölçeği kamera mesafesiyle
        // (ortografikte zoom'la) orantıla → zoom'dan bağımsız sabit boyutta görünür. Genişlik
        // canvas en-boy oranıyla çarpılır → çerçeve bozulmaz (büzük/eğik olmaz).
        const aspect = canvas.width / Math.max(1, canvas.height);
        const EKRAN_ORAN = 0.045; // viewport yüksekliğine oranlı sabit boyut (büyüt/küçült için ayarlanır)
        const spr = this.frameSprite;
        spr.scale.set(EKRAN_ORAN * aspect, EKRAN_ORAN, 1); // ilk frame için makul değer
        spr.onBeforeRender = (_renderer, _scene, camera: any) => {
            let h: number;
            if (camera?.isOrthographicCamera) {
                const viewH = (camera.top - camera.bottom) / (camera.zoom || 1);
                h = viewH * EKRAN_ORAN;
            } else {
                const d = camera.position.distanceTo(spr.position) || 1;
                h = d * EKRAN_ORAN;
            }
            spr.scale.set(h * aspect, h, 1);
        };

        // Raycast için userData
        this.frameSprite.userData = {
            isGDTSymbol: true,
            parent: this
        };

        this.add(this.frameSprite);
    }

    /**
     * Leader çizgisi oluştur
     */
    private createLeaderLine(): void {
        const positions = [
            this.attachPoint.x, this.attachPoint.y, this.attachPoint.z,
            this.framePosition.x, this.framePosition.y, this.framePosition.z
        ];

        const geometry = new LineGeometry();
        geometry.setPositions(positions);

        const material = new LineMaterial({
            color: this.style.leaderColor,
            linewidth: this.style.leaderLineWidth * 0.001,
            resolution: getCanvasResolutionVec2()
        });

        this.leaderLine = new Line2(geometry, material);
        this.leaderLine.computeLineDistances();
        this.add(this.leaderLine);
    }

    /**
     * Leader ok ucu oluştur
     */
    private createLeaderArrow(): void {
        const direction = this.framePosition.clone().sub(this.attachPoint).normalize();
        const length = this.style.leaderArrowSize;
        const width = length * 0.4;

        const shape = new THREE.Shape();
        shape.moveTo(0, 0);
        shape.lineTo(-length, width / 2);
        shape.lineTo(-length * 0.7, 0);
        shape.lineTo(-length, -width / 2);
        shape.closePath();

        const geometry = new THREE.ShapeGeometry(shape);
        const material = new THREE.MeshBasicMaterial({
            color: this.style.leaderColor,
            side: THREE.DoubleSide
        });

        this.leaderArrow = new THREE.Mesh(geometry, material);
        this.leaderArrow.position.copy(this.attachPoint);

        // Yönlendirme
        const angle = Math.atan2(direction.y, direction.x);
        this.leaderArrow.rotation.z = angle + Math.PI;

        this.add(this.leaderArrow);
    }

    /**
     * Leader çizgisini güncelle
     */
    private updateLeaderLine(): void {
        if (this.leaderLine) {
            const positions = [
                this.attachPoint.x, this.attachPoint.y, this.attachPoint.z,
                this.framePosition.x, this.framePosition.y, this.framePosition.z
            ];

            const geometry = new LineGeometry();
            geometry.setPositions(positions);

            this.leaderLine.geometry.dispose();
            this.leaderLine.geometry = geometry;
            this.leaderLine.computeLineDistances();
        }

        if (this.leaderArrow) {
            const direction = this.framePosition.clone().sub(this.attachPoint).normalize();
            const angle = Math.atan2(direction.y, direction.x);
            this.leaderArrow.rotation.z = angle + Math.PI;
        }
    }

    /**
     * Görsel durumu güncelle
     */
    private updateVisualState(): void {
        // Frame'i yeniden oluştur
        if (this.frameSprite) {
            const mat = this.frameSprite.material as THREE.SpriteMaterial;
            if (mat.map) mat.map.dispose();
            mat.dispose();
            this.remove(this.frameSprite);
            this.frameSprite = null;
        }
        this.createFeatureControlFrame();
    }
}

// ============================================================================
// DATUM REFERENCE CLASS
// ============================================================================

/**
 * FikoDatumReference - Datum referans kutusu (A, B, C)
 */
export class FikoDatumReference extends THREE.Group {
    readonly symbolType = 'FikoDatumReference';

    private label: string;
    private position_: THREE.Vector3;
    private style: GDTStyleConfig;
    private interactive: InteractiveConfig;

    private frameSprite: THREE.Sprite | null = null;
    private leaderLine: Line2 | null = null;
    private triangleMarker: THREE.Mesh | null = null;

    // İnteraktif durum
    private _isSelected: boolean = false;
    private _isHovered: boolean = false;
    private _isDragging: boolean = false;

    constructor(
        label: string,
        attachPoint: THREE.Vector3,
        frameOffset: THREE.Vector3 = new THREE.Vector3(15, 0, 0),
        style: Partial<GDTStyleConfig> = {},
        interactive: Partial<InteractiveConfig> = {}
    ) {
        super();

        this.label = label;
        this.position_ = attachPoint.clone().add(frameOffset);

        this.style = { ...CATIA_GDT_STYLE, ...style };
        this.interactive = {
            draggable: true,
            selectable: true,
            hoverable: true,
            ...interactive
        };

        this.renderOrder = 1001;
        this.frustumCulled = false;

        this.build(attachPoint);
    }

    /**
     * Seç/Seçimi kaldır
     */
    public setSelected(selected: boolean): void {
        this._isSelected = selected;
        this.updateVisualState();
    }

    /**
     * Hover durumunu ayarla
     */
    public setHovered(hovered: boolean): void {
        if (!this.interactive.hoverable) return;
        this._isHovered = hovered;
        this.updateVisualState();
    }

    /**
     * Temizlik
     */
    public dispose(): void {
        if (this.frameSprite) {
            const mat = this.frameSprite.material as THREE.SpriteMaterial;
            if (mat.map) mat.map.dispose();
            mat.dispose();
            this.remove(this.frameSprite);
        }

        if (this.leaderLine) {
            this.leaderLine.geometry.dispose();
            (this.leaderLine.material as THREE.Material).dispose();
            this.remove(this.leaderLine);
        }

        if (this.triangleMarker) {
            this.triangleMarker.geometry.dispose();
            (this.triangleMarker.material as THREE.Material).dispose();
            this.remove(this.triangleMarker);
        }
    }

    private build(attachPoint: THREE.Vector3): void {
        // Datum kutusu (kare içinde harf)
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        canvas.width = 64;
        canvas.height = 64;

        // Arka plan
        ctx.fillStyle = this._isSelected ? '#ffff00' :
                        this._isHovered ? '#e0e0e0' :
                        `#${this.style.backgroundColor.toString(16).padStart(6, '0')}`;
        ctx.fillRect(8, 8, 48, 48);

        // Çerçeve
        ctx.strokeStyle = this.style.textColor;
        ctx.lineWidth = 3;
        ctx.strokeRect(8, 8, 48, 48);

        // Harf
        ctx.font = `bold 32px ${this.style.fontFamily}`;
        ctx.fillStyle = this.style.textColor;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(this.label, 32, 32);

        const texture = new THREE.CanvasTexture(canvas);
        const spriteMaterial = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            depthTest: false
        });

        this.frameSprite = new THREE.Sprite(spriteMaterial);
        (this.frameSprite as any).raycast = () => {}; // 🎯 ölçüm tıklamasını engelleme
        this.frameSprite.position.copy(this.position_);
        this.frameSprite.scale.set(6, 6, 1);
        this.add(this.frameSprite);

        // Leader çizgisi
        this.createLeaderLine(attachPoint);

        // Üçgen işaretçi (yüzeye bağlantı noktası)
        this.createTriangleMarker(attachPoint);
    }

    private createLeaderLine(attachPoint: THREE.Vector3): void {
        const positions = [
            attachPoint.x, attachPoint.y, attachPoint.z,
            this.position_.x - 3, this.position_.y, this.position_.z
        ];

        const geometry = new LineGeometry();
        geometry.setPositions(positions);

        const material = new LineMaterial({
            color: this.style.leaderColor,
            linewidth: 0.001,
            resolution: getCanvasResolutionVec2()
        });

        this.leaderLine = new Line2(geometry, material);
        this.leaderLine.computeLineDistances();
        this.add(this.leaderLine);
    }

    private createTriangleMarker(attachPoint: THREE.Vector3): void {
        const size = 2;
        const shape = new THREE.Shape();
        shape.moveTo(0, 0);
        shape.lineTo(-size, -size);
        shape.lineTo(-size, size);
        shape.closePath();

        const geometry = new THREE.ShapeGeometry(shape);
        const material = new THREE.MeshBasicMaterial({
            color: this.style.leaderColor,
            side: THREE.DoubleSide
        });

        this.triangleMarker = new THREE.Mesh(geometry, material);
        this.triangleMarker.position.copy(attachPoint);
        this.add(this.triangleMarker);
    }

    private updateVisualState(): void {
        this.dispose();
        // Rebuild with attachPoint approximation
        const attachPoint = this.position_.clone().sub(new THREE.Vector3(15, 0, 0));
        this.build(attachPoint);
    }
}

// ============================================================================
// GD&T MANAGER CLASS
// ============================================================================

/**
 * GDT Manager - GD&T sembolleri ve datum referanslarını yönetir
 */
export class FikoGDTManager {
    private scene: THREE.Scene | THREE.Group;
    private symbols: Map<string, FikoGDTSymbol> = new Map();
    private datums: Map<string, FikoDatumReference> = new Map();
    private style: GDTStyleConfig;

    // Raycast için
    private raycaster: THREE.Raycaster = new THREE.Raycaster();
    private mouse: THREE.Vector2 = new THREE.Vector2();

    // Aktif sürükleme
    private activeSymbol: FikoGDTSymbol | null = null;

    constructor(scene: THREE.Scene | THREE.Group, style: Partial<GDTStyleConfig> = {}) {
        this.scene = scene;
        this.style = { ...CATIA_GDT_STYLE, ...style };
    }

    /**
     * GD&T sembolü ekle
     */
    public addSymbol(
        id: string,
        fcfConfig: FCFConfig,
        attachPoint: THREE.Vector3,
        frameOffset?: THREE.Vector3
    ): FikoGDTSymbol {
        const symbol = new FikoGDTSymbol(
            fcfConfig,
            attachPoint,
            frameOffset,
            this.style
        );

        symbol.setEventListeners({
            onDragStart: (s) => this.onSymbolDragStart(s),
            onDrag: (s, pos) => this.onSymbolDrag(s, pos),
            onDragEnd: (s) => this.onSymbolDragEnd(s),
            onSelect: (s) => this.onSymbolSelect(s),
            onHover: (s, h) => this.onSymbolHover(s, h)
        });

        this.symbols.set(id, symbol);
        this.scene.add(symbol);

        return symbol;
    }

    /**
     * Datum referansı ekle
     */
    public addDatum(
        id: string,
        label: string,
        attachPoint: THREE.Vector3,
        frameOffset?: THREE.Vector3
    ): FikoDatumReference {
        const datum = new FikoDatumReference(
            label,
            attachPoint,
            frameOffset,
            this.style
        );

        this.datums.set(id, datum);
        this.scene.add(datum);

        return datum;
    }

    /**
     * Sembol kaldır
     */
    public removeSymbol(id: string): void {
        const symbol = this.symbols.get(id);
        if (symbol) {
            symbol.dispose();
            this.scene.remove(symbol);
            this.symbols.delete(id);
        }
    }

    /**
     * Datum kaldır
     */
    public removeDatum(id: string): void {
        const datum = this.datums.get(id);
        if (datum) {
            datum.dispose();
            this.scene.remove(datum);
            this.datums.delete(id);
        }
    }

    /**
     * Tümünü temizle
     */
    public clear(): void {
        this.symbols.forEach((symbol, id) => {
            symbol.dispose();
            this.scene.remove(symbol);
        });
        this.symbols.clear();

        this.datums.forEach((datum, id) => {
            datum.dispose();
            this.scene.remove(datum);
        });
        this.datums.clear();
    }

    /**
     * Mouse olaylarını işle
     */
    public handleMouseMove(event: MouseEvent, camera: THREE.Camera, domElement: HTMLElement): void {
        const rect = domElement.getBoundingClientRect();
        this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        if (this.activeSymbol?.isDragging) {
            // Sürükleme devam ediyor
            const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
            const intersection = new THREE.Vector3();
            this.raycaster.setFromCamera(this.mouse, camera);
            this.raycaster.ray.intersectPlane(plane, intersection);

            if (intersection) {
                this.activeSymbol.drag(intersection);
            }
        } else {
            // Hover kontrolü
            this.checkHover(camera);
        }
    }

    public handleMouseDown(event: MouseEvent, camera: THREE.Camera, domElement: HTMLElement): void {
        const rect = domElement.getBoundingClientRect();
        this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        const symbol = this.getSymbolUnderMouse(camera);
        if (symbol) {
            // Seçimi değiştir
            this.deselectAll();
            symbol.setSelected(true);

            // Sürüklemeyi başlat
            const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
            const intersection = new THREE.Vector3();
            this.raycaster.setFromCamera(this.mouse, camera);
            this.raycaster.ray.intersectPlane(plane, intersection);

            if (intersection) {
                symbol.startDrag(intersection);
                this.activeSymbol = symbol;
            }
        }
    }

    public handleMouseUp(): void {
        if (this.activeSymbol?.isDragging) {
            this.activeSymbol.endDrag();
            this.activeSymbol = null;
        }
    }

    /**
     * Temizlik
     */
    public dispose(): void {
        this.clear();
    }

    // ========================================================================
    // PRIVATE
    // ========================================================================

    private checkHover(camera: THREE.Camera): void {
        const symbol = this.getSymbolUnderMouse(camera);

        this.symbols.forEach(s => {
            s.setHovered(s === symbol);
        });
    }

    private getSymbolUnderMouse(camera: THREE.Camera): FikoGDTSymbol | null {
        this.raycaster.setFromCamera(this.mouse, camera);

        const objects: THREE.Object3D[] = [];
        this.symbols.forEach(s => {
            s.traverse(obj => {
                if (obj.userData.isGDTSymbol) {
                    objects.push(obj);
                }
            });
        });

        const intersects = this.raycaster.intersectObjects(objects, true);
        if (intersects.length > 0) {
            const obj = intersects[0].object;
            if (obj.userData.parent instanceof FikoGDTSymbol) {
                return obj.userData.parent;
            }
        }

        return null;
    }

    private deselectAll(): void {
        this.symbols.forEach(s => s.setSelected(false));
        this.datums.forEach(d => d.setSelected(false));
    }

    private onSymbolDragStart(symbol: FikoGDTSymbol): void {
        // console.log('Drag started');
    }

    private onSymbolDrag(symbol: FikoGDTSymbol, position: THREE.Vector3): void {
        // Drag update
    }

    private onSymbolDragEnd(symbol: FikoGDTSymbol): void {
        // console.log('Drag ended');
    }

    private onSymbolSelect(symbol: FikoGDTSymbol): void {
        // console.log('Symbol selected');
    }

    private onSymbolHover(symbol: FikoGDTSymbol, isHovered: boolean): void {
        document.body.style.cursor = isHovered ? 'pointer' : 'default';
    }
}

// ============================================================================
// FACTORY FUNCTIONS
// ============================================================================

/**
 * Düzlemsellik GD&T sembolü oluştur
 */
export function createFlatnessSymbol(
    attachPoint: THREE.Vector3,
    tolerance: number,
    datumRef?: string,
    offset?: THREE.Vector3
): FikoGDTSymbol {
    return new FikoGDTSymbol(
        {
            symbolType: GDTSymbolType.FLATNESS,
            tolerance,
            datumRefs: datumRef ? [datumRef] : undefined
        },
        attachPoint,
        offset
    );
}

/**
 * Diklik GD&T sembolü oluştur
 */
export function createPerpendicularitySymbol(
    attachPoint: THREE.Vector3,
    tolerance: number,
    datumRefs: string[],
    offset?: THREE.Vector3
): FikoGDTSymbol {
    return new FikoGDTSymbol(
        {
            symbolType: GDTSymbolType.PERPENDICULARITY,
            tolerance,
            datumRefs
        },
        attachPoint,
        offset
    );
}

/**
 * Paralellik GD&T sembolü oluştur
 */
export function createParallelismSymbol(
    attachPoint: THREE.Vector3,
    tolerance: number,
    datumRefs: string[],
    offset?: THREE.Vector3
): FikoGDTSymbol {
    return new FikoGDTSymbol(
        {
            symbolType: GDTSymbolType.PARALLELISM,
            tolerance,
            datumRefs
        },
        attachPoint,
        offset
    );
}

/**
 * Pozisyon GD&T sembolü oluştur
 */
export function createPositionSymbol(
    attachPoint: THREE.Vector3,
    tolerance: number,
    datumRefs: string[],
    diameter: boolean = true,
    materialCondition: MaterialCondition = MaterialCondition.MMC,
    offset?: THREE.Vector3
): FikoGDTSymbol {
    return new FikoGDTSymbol(
        {
            symbolType: GDTSymbolType.POSITION,
            tolerance,
            datumRefs,
            diameter,
            materialCondition
        },
        attachPoint,
        offset
    );
}

// All exports are inline with their declarations above
